import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_CONTENT_BY_SLUG, GET_CONTENTS, GET_AUDIO_NATIVE_SETTINGS } from '../services/queries';
import Breadcrumb from '../components/Breadcrumb';
import SocialShare from '../components/SocialShare';
import RelatedArticles from '../components/RelatedArticles';
import ElevenLabsAudioNative from '../components/ElevenLabsAudioNative';
import { getStaticArticleBySlug } from '../data/staticArticles';

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  // 🔍 Query GraphQL per recuperare l'articolo dal database
  const { loading, error, data } = useQuery(GET_CONTENT_BY_SLUG, {
    variables: { slug: slug || '' },
    skip: !slug, // Salta la query se non c'è slug
    errorPolicy: 'all' // Continua anche se ci sono errori
  });

  // 📰 Query per recuperare tutti gli articoli (per articoli correlati)
  const { data: allArticlesData } = useQuery(GET_CONTENTS, {
    variables: { limit: 50, status: 'PUBLISHED' },
    errorPolicy: 'all'
  });

  // 🔊 Query per impostazioni Audio Native
  const { data: audioNativeData } = useQuery(GET_AUDIO_NATIVE_SETTINGS, {
    errorPolicy: 'all'
  });

  console.log('🔍 BlogDetailPage Debug:', {
    slug,
    loading,
    error: error?.message,
    data: data?.content,
    hasGraphQLArticle: !!data?.content
  });

  console.log('🔊 Audio Native Debug:', {
    audioNativeData: audioNativeData?.audioNativeSettings,
    enabled: audioNativeData?.audioNativeSettings?.enabled,
    publicUserId: audioNativeData?.audioNativeSettings?.publicUserId
  });

  // 🔄 Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Caricamento articolo...</p>
        </div>
      </div>
    );
  }

  // 📝 Determina quale articolo usare (GraphQL o statico)
  const graphqlArticle = data?.content;

  let article: any = null;

  if (graphqlArticle) {
    // ✅ Articolo trovato nel database GraphQL
    article = {
      id: graphqlArticle.id,
      title: graphqlArticle.title,
      content: graphqlArticle.body || graphqlArticle.excerpt || 'Contenuto non disponibile',
      date: graphqlArticle.publishedAt
        ? new Date(graphqlArticle.publishedAt).toLocaleDateString('it-IT')
        : new Date(graphqlArticle.createdAt).toLocaleDateString('it-IT'),
      tag: graphqlArticle.category?.name || 'Generale',
      image: graphqlArticle.featuredImage || '/images/logo-unasca.png',
      author: graphqlArticle.author?.profile?.fullName || graphqlArticle.author?.username || 'UNASCA',
      readingTime: graphqlArticle.readingTime || 3,
      attachments: graphqlArticle.attachments || []
    };
    console.log('✅ Usando articolo GraphQL:', article.title);
  } else {
    // 🔄 Fallback agli articoli statici dalla nuova struttura modulare
    const staticArticle = slug ? getStaticArticleBySlug(slug) : null;

    if (staticArticle) {
      article = {
        id: staticArticle.id,
        title: staticArticle.title,
        content: staticArticle.body,
        date: new Date(staticArticle.publishedAt).toLocaleDateString('it-IT', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        tag: staticArticle.category.name,
        image: staticArticle.featuredImage,
        author: staticArticle.author.profile.fullName,
        readingTime: staticArticle.readingTime
      };
      console.log('✅ Usando articolo statico:', article.title);
    } else {
      console.log(`❌ Articolo non trovato per slug: "${slug}"`);
    }
  }

  // ❌ Se l'articolo non esiste né nel database né negli statici
  if (!article) {
    console.log(`❌ Articolo non trovato per slug: "${slug}"`);
    return <Navigate to="/blog" replace />;
  }

  // 🔊 L'audio ElevenLabs è consentito solo per articoli pubblicati con il flag attivo.
  // Articoli pre-esistenti (audioEnabled null/undefined) restano attivi; articoli statici
  // (nessun graphqlArticle) mantengono il comportamento precedente.
  const audioAllowed = graphqlArticle
    ? (graphqlArticle.status === 'PUBLISHED' && graphqlArticle.audioEnabled !== false)
    : true;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section - Escluso dalla lettura audio */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20" data-elevenlabs-ignore="true">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center space-x-2 text-primary-200 mb-4">
              <Link to="/blog" className="hover:text-white transition-colors">
                Blog
              </Link>
              <span>/</span>
              <span>{article.tag}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-primary-100">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{article.date}</span>
              </div>
              {article.author && (
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{article.author}</span>
                </div>
              )}
              {article.readingTime && (
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{article.readingTime} min di lettura</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <div className="container-custom py-12">
        <div className="max-w-4xl mx-auto">
          {/* 📈 Breadcrumb */}
          <Breadcrumb
            items={[
              { label: 'Home', href: '/' },
              { label: 'Blog', href: '/blog' },
              { label: article.title, current: true }
            ]}
            className="mb-8"
          />
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            {/* Article Image */}
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto"
              onError={(e) => {
                e.currentTarget.src = '/images/logo-unasca.png';
              }}
            />

            {/* Article Body */}
            <div className="p-8 md:p-12">
              {/* 🔊 Audio Player ElevenLabs - Escluso dalla lettura */}
              {audioAllowed &&
               audioNativeData?.audioNativeSettings?.enabled &&
               audioNativeData?.audioNativeSettings?.publicUserId && (
                <div className="mb-8 pb-6 border-b border-gray-200 dark:border-gray-700" data-elevenlabs-ignore="true">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 0112.728 0" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Ascolta l'articolo
                    </span>
                  </div>
                  <ElevenLabsAudioNative
                    publicUserId={audioNativeData.audioNativeSettings.publicUserId}
                    size="small"
                  />
                </div>
              )}
              <div
                className="unasca-article prose prose-lg dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* 📎 Allegati */}
              {article.attachments && article.attachments.length > 0 && (
                <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-600">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Allegati
                  </h3>
                  <div className="space-y-2">
                    {article.attachments.map((attachment: any, index: number) => {
                      const getFileIcon = (mimeType: string) => {
                        if (mimeType === 'application/pdf') return '📄';
                        if (mimeType.includes('word')) return '📝';
                        return '📎';
                      };

                      const formatFileSize = (bytes: number) => {
                        if (bytes === 0) return '0 Bytes';
                        const k = 1024;
                        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                        const i = Math.floor(Math.log(bytes) / Math.log(k));
                        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
                      };

                      return (
                        <a
                          key={index}
                          href={`${(process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace('/api', '')}${attachment.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors group"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <span className="text-2xl flex-shrink-0">
                              {getFileIcon(attachment.mimeType)}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400">
                                {attachment.originalName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatFileSize(attachment.size)}
                              </p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 flex-shrink-0 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 🔗 Condivisione Social */}
              <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-600">
                <SocialShare
                  url={`${window.location.origin}/blog/${slug}`}
                  title={article.title}
                  description={article.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...'}
                  className="justify-center"
                />
              </div>
            </div>
          </div>

          {/* 📰 Articoli Correlati */}
          {(() => {
            // Combina articoli GraphQL e statici per RelatedArticles
            const allArticles = [
              ...(allArticlesData?.contents || [])
            ];

            return allArticles.length > 1 ? (
              <RelatedArticles
                currentArticle={{
                  id: article.id,
                  title: article.title,
                  excerpt: article.content.replace(/<[^>]*>/g, '').substring(0, 150) + '...',
                  slug: slug,
                  tag: article.tag
                }}
                allArticles={allArticles}
                maxArticles={3}
                className="mt-12"
              />
            ) : null;
          })()}

          {/* Navigation */}
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-between">
            <Link
              to="/blog"
              className="inline-flex items-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Torna al Blog
            </Link>

            <Link
              to="/contact"
              className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Contattaci
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetailPage;
